'use client';

import { usePathname } from 'next/navigation';
import { localizeAnchorHref } from '@/lib/constants';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import type { SiteNavigationData } from '@/sanity/types';

interface MobileMenuProps {
  navigation: SiteNavigationData;
}

/**
 * Mobile/tablet menu with hamburger toggle, slide-out panel, and focus trap.
 * Visible below xl breakpoint. Receives navigation data from the server.
 */
export function MobileMenu({ navigation }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, close]);

  return (
    <div className="lg:hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls="mobile-menu"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        data-analytics-cta="mobile_menu_toggle"
        data-analytics-location="header"
        className="relative z-50 flex size-10 flex-col items-center justify-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <motion.span
          animate={isOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
          className="block h-[2px] w-6 bg-foreground"
        />
        <motion.span
          animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
          className="block h-[2px] w-6 bg-foreground"
        />
        <motion.span
          animate={isOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
          className="block h-[2px] w-6 bg-foreground"
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
              className="fixed left-0 top-0 z-40 h-[100dvh] w-screen max-w-[100vw] bg-background/80 backdrop-blur-sm"
            />

            <motion.nav
              id="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed right-0 top-0 z-40 flex h-[100dvh] w-[85vw] max-w-md flex-col overflow-y-auto bg-background px-8 pb-10 pt-24"
              aria-label="Mobile navigation"
            >
              {navigation.mainNav.length > 0 ? (
                <>
                  <p className="mb-4 text-[12px] font-normal uppercase tracking-widest text-muted">
                    Menu
                  </p>
                  <ul className="flex flex-col gap-4">
                    {navigation.mainNav.map((link) => {
                      const children = link.children ?? [];
                      return (
                        <li key={link.href}>
                          <Link
                            href={link.href}
                            onClick={close}
                            data-analytics-cta="mobile_menu_main_nav"
                            data-analytics-location="mobile_menu"
                            data-analytics-label={link.label}
                            className="block min-h-[44px] py-2 text-[22px] font-light text-foreground transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            {link.label}
                          </Link>
                          {children.length > 0 ? (
                            <ul className="mt-1 flex flex-col border-l border-border/20 pl-4">
                              {children.map((child) => (
                                <li key={child.href}>
                                  <Link
                                    href={child.href}
                                    onClick={close}
                                    data-analytics-cta="mobile_menu_main_nav_child"
                                    data-analytics-location="mobile_menu"
                                    data-analytics-label={child.label}
                                    className="block min-h-[40px] py-1.5 text-[17px] font-light text-muted transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                  >
                                    {child.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : null}

              {navigation.topNav.length > 0 ? (
                <>
                  <div className="my-6 h-px bg-border/20" />
                  <p className="mb-4 text-[12px] font-normal uppercase tracking-widest text-muted">
                    Company
                  </p>
                  <ul className="flex flex-col gap-3">
                    {navigation.topNav.map((link) => {
                      const children = link.children ?? [];
                      return (
                        <li key={link.href}>
                          <Link
                            href={link.href}
                            onClick={close}
                            data-analytics-cta="mobile_menu_top_nav"
                            data-analytics-location="mobile_menu"
                            data-analytics-label={link.label}
                            className="block min-h-[44px] py-2 text-[18px] font-light text-muted transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            {link.label}
                          </Link>
                          {children.length > 0 ? (
                            <ul className="mt-1 flex flex-col border-l border-border/20 pl-4">
                              {children.map((child) => (
                                <li key={child.href}>
                                  <Link
                                    href={child.href}
                                    onClick={close}
                                    data-analytics-cta="mobile_menu_top_nav_child"
                                    data-analytics-location="mobile_menu"
                                    data-analytics-label={child.label}
                                    className="block min-h-[40px] py-1.5 text-[16px] font-light text-muted transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                  >
                                    {child.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : null}

              {navigation.cta ? (
                <div className="mt-8">
                  <Link
                    href={localizeAnchorHref(navigation.cta.href, pathname)}
                    onClick={close}
                    data-analytics-cta="mobile_menu_cta"
                    data-analytics-location="mobile_menu"
                    data-analytics-label={navigation.cta.label}
                    className="inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 text-[16px] font-normal uppercase tracking-[-0.02em] text-background transition-colors hover:bg-accent-dark"
                  >
                    {navigation.cta.label}
                  </Link>
                </div>
              ) : null}
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
